/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useState } from 'react';
import { Accordion, Button, Select } from '@cimpress/react-components';
import { connect } from 'react-redux';
import { toSelectOption } from 'src/util/misc';
import type { RootState } from 'src/store';
import type { LinkType } from 'src/types/link';
import ConfigurationSku from './ConfigurationSku';
import { deleteSceneVariation, loadLink, setCreateMode } from './slice';
import getLinks from './selectors/getSceneLinks';
import getShowForm from './selectors/getCreateMode';
import getSceneVariation from './selectors/getSceneVariation';
import ConfirmModal from '../confirm';

interface Props {
    showForm: boolean;
    linkId?: string;
    sceneId?: string;
    sceneVariableId?: string;
    links: LinkType[];
    loadLink: (linkId: string) => void;
    setCreateMode: (show: boolean) => void;
    // The underlying reducer takes no payload (RTK types the created action creator's parameter as
    // `void`), but this component calls it positionally with a payload object anyway (the reducer
    // ignores it) -- `any` is required here since no stricter parameter type is assignable from a
    // `void`-parameter action creator.
    deleteScene: (payload: any) => void;
}

const SceneVariation = (props: Props) => {
    const [showConfirm, setShowConfirm] = useState(false);
    const linkOptions = props.links.map((link) =>
        toSelectOption(`${link.sku}-${link.skuVersion} ${link.linkId}`, link.linkId),
    );

    const createNew = (show: boolean) => props.setCreateMode(show);
    const loadData = (selected: { value: string } | null) => {
        if (selected) {
            props.loadLink(selected.value);
        }
    };

    useEffect(() => {
        if (!props.links || props.links.length === 0) {
            props.setCreateMode(true);
        }
    }, [props.links]);

    const loadLinks = useMemo(() => {
        if (!props.showForm && props.links && props.links.length > 0) {
            return (
                <div style={{ padding: '8px' }}>
                    <h3>Load a Configuration</h3>
                    <Select options={linkOptions} label='Select a Configuration' onChange={loadData} />

                    <Button variant='primary' onClick={() => createNew(true)}>
                        Create New Configuration
                    </Button>
                </div>
            );
        }
        if (props.links && props.links.length > 0) {
            return (
                <Button style={{ padding: '8px' }} variant='anchor' onClick={() => createNew(false)}>
                    &lt; Go Back
                </Button>
            );
        }
        return <></>;
    }, [props.links, props.showForm]);

    const confirmDelete = (evt: React.MouseEvent<HTMLButtonElement>) => {
        evt.stopPropagation();
        setShowConfirm(true);
    };

    const title = (
        <div className='layers-header'>
            <div className='layers-header__text'>Scene Configuration</div>
            {props.linkId && (
                <button onClick={confirmDelete}>
                    <img className='iconLg' src='https://static.ux.cimpress.io/assets/icons/bin-1-l.svg' />
                </button>
            )}
        </div>
    );

    const handleDelete = (confirmation: boolean) => {
        if (confirmation) {
            const { sceneVariableId, sceneId, linkId, deleteScene } = props;
            deleteScene({ sceneId, linkId, sceneVariableId });
        }

        setShowConfirm(false);
    };

    return (
        <div className='layers-panel scene-variation'>
            {loadLinks}
            {props.showForm && (
                <Accordion title={title} defaultOpen={true}>
                    <div className='sku-configuration'>
                        <ConfigurationSku />
                    </div>
                </Accordion>
            )}
            <ConfirmModal
                title='Do you want to Delete the Configuration?'
                showModal={showConfirm}
                onConfirm={handleDelete}
                status='danger'>
                <>
                    <p>
                        This will delete all the links attached to the Scene Variation and any data related to this
                        Scene Variation. Are you sure you want to Continue?
                    </p>
                </>
            </ConfirmModal>
        </div>
    );
};

function mapStateToProps(state: RootState) {
    const { sceneLinkId, sceneId, sceneVariableId } = getSceneVariation(state);
    const links = getLinks(state);
    const showForm = getShowForm(state);
    return {
        // `getLinks` is typed to return `any[]` by the slice (`links: any[]`), but the saga only ever
        // populates it with `LinkType[]` results from `getLinksForAsset` (see saga.ts's
        // `loadLinksForScene`) -- asserting the real shape here instead of leaving it `any`.
        links: links as LinkType[],
        showForm,
        linkId: sceneLinkId,
        sceneVariableId,
        sceneId,
    };
}

export default connect(mapStateToProps, {
    loadLink,
    setCreateMode,
    deleteScene: deleteSceneVariation,
})(SceneVariation);
